import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { ReportMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { searchCollectorReport } from '@/adapters/report';
import { ICollectorReportReq, ICollectorReportRes } from '@/interfaces/report';
import Search from 'antd/es/input/Search';
import CollectorReportTable from './components/collector-report-table';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import UserSelector from '@/components/selector/user-selector';
import { Button, DatePicker, notification, Select } from 'antd';
import MediaUploader from '@/components/media/uploader-modal';
import {
  IUpdatePaymentReceived,
  IUploadPaymentRecieved,
} from '@/interfaces/collector';
import { uploadPaymentReceived } from '@/adapters/collector';
import { EPaymentType } from '@/enums/ECollector';
import PaymentReceivedModal from './components/payment-received-modal';
import { IDataModal } from '@/interfaces/dataModal';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs, { Dayjs } from 'dayjs';
import { getPaymentReceivedStatusOptions } from '@/helpers/orderHelper';
import ExcelExport from './components/export-report';
import { useAuth } from '@/contexts/AuthContext';

const _criteria: ICollectorReportReq = {
  search: '',
  startDate: dayjs().startOf('month').add(-4, 'month'),
  endDate: dayjs().endOf('month'),
};

const _modal: IDataModal<IUpdatePaymentReceived> = {
  open: false,
};

const StockReport: React.FC = () => {
  const pathSegment = getPathSegment();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ICollectorReportReq>({
    ..._criteria,
    businessUnitId: userInfo?.bu || undefined,
  });
  const [margin] = useState<number>(0);
  const [cheque, setCheque] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isUpload, setIsUpload] = useState(false);
  const [dataModal, setDataModal] =
    useState<IDataModal<IUpdatePaymentReceived>>(_modal);

  const { data, isLoading, refetch } = useQuery<
    AxiosResponse<ICollectorReportRes[]>,
    Error
  >({
    queryKey: ['search-collector-report', criteria],
    queryFn: async () => await searchCollectorReport(criteria),
  });

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
    });
  };

  const reports = (data?.data || []).map((c, idx) => {
    c.orderId = idx + 1;
    return c;
  });

  const onEditPayment = (rec: ICollectorReportRes) => {
    const paymentReceived: IUpdatePaymentReceived = {
      ivNo: rec.ivNo,
      billingDate: rec.billingDate,
      billingNo: rec.billingNo as string,
      chequeNo: rec.chequeNo as string,
      id: rec.id as number,
      paidDate: rec.paidDate,
      remark: rec.remark as string,
      status: rec.status,
      paidAmount: rec.paidAmount,
    };
    setDataModal({
      open: true,
      id: rec.id,
      item: paymentReceived,
    });
  };

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    rows: ICollectorReportRes[],
  ) => {
    if (selectedRowKeys.length) {
      setSelectedRowKeys([]);
    } else {
      if (rows.length) {
        const paymentType = rows[0].paymentType;
        if (paymentType === EPaymentType.CHEQUE) {
          const chequeNo = rows[0].chequeNo;
          if (chequeNo) {
            const selectedRows = reports.filter(r => r.chequeNo === chequeNo);
            const selectedRowKeys = selectedRows.map(c => {
              return c.id as number;
            });
            setSelectedRowKeys(selectedRowKeys);
          }
        } else {
          const billingNo = rows[0].billingNo;
          if (billingNo) {
            const selectedRows = reports.filter(r => r.billingNo === billingNo);
            const selectedRowKeys = selectedRows.map(c => {
              return c.id as number;
            });
            setSelectedRowKeys(selectedRowKeys);
          } else {
            setSelectedRowKeys(newSelectedRowKeys);
          }
        }
      }
    }
  };

  const mutation = useMutation({
    mutationFn: async (data: IUploadPaymentRecieved) =>
      await uploadPaymentReceived(data),
    mutationKey: ['upload-payment-received'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'อัพโหลด Cheque สำเร็จ',
        });
        setSelectedRowKeys([]);
        setCheque(false);
        refetch();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const onUploadPaymentReceived = (id: number) => {
    const model: IUploadPaymentRecieved = {
      paymentsReceivedIds: selectedRowKeys as number[],
      receivedId: id,
    };
    mutation.mutateAsync(model);
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        startDate: undefined,
        endDate: undefined,
      });
    }
  };

  return (
    <>
      <SubMenu active={pathSegment} items={ReportMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Collector Report">
          <div className="flex gap-3">
            {!cheque ? (
              <Button type="primary" onClick={() => setCheque(!cheque)}>
                แนบไฟล์เช็ค/สลิป
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  type="primary"
                  onClick={() => setIsUpload(true)}
                  loading={mutation?.isPending}
                  disabled={!selectedRowKeys?.length}
                >
                  อัพโหลดเช็ค/สลิป
                </Button>
                <Button ghost type="primary" onClick={() => setCheque(!cheque)}>
                  ยกเลิก
                </Button>
              </div>
            )}
            <ExcelExport data={reports} />
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <BusinessUnitSelector
                isAll
                handleSelectData={id => {
                  setCriteria({
                    ...criteria,
                    businessUnitId: id,
                  });
                }}
                disabled={!!userInfo?.bu}
                value={criteria.businessUnitId}
              />
              <DatePicker.RangePicker
                size="large"
                locale={locale}
                onChange={onRangeChange}
                defaultValue={[
                  dayjs(criteria.startDate),
                  dayjs(criteria.endDate),
                ]}
              />
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={onSearch}
              />
              <Select
                allowClear
                className="md:min-w-[150px]"
                size="large"
                placeholder="เลือก สถานะ"
                options={getPaymentReceivedStatusOptions()}
                onChange={e =>
                  setCriteria({
                    ...criteria,
                    Status: e,
                  })
                }
              />

              <UserSelector
                businessUnitId={criteria.businessUnitId}
                customClassName="md:w-[250px]"
                handleSelectData={function (id: string): void {
                  setCriteria({
                    ...criteria,
                    salesId: id,
                  });
                }}
                isAll
                allowClear
              />
            </div>
          </div>
          <CollectorReportTable
            margin={margin}
            report={reports || []}
            loading={isLoading}
            onTableChange={() => console.log()}
            onSelectChange={onSelectChange}
            selectedRowKeys={selectedRowKeys}
            isSelect={cheque}
            onEdit={record => onEditPayment(record)}
          />
          <MediaUploader
            data={{
              modelId: 1,
              modelType: 'cheque',
            }}
            open={isUpload}
            onCancel={() => setIsUpload(false)}
            onUploaded={id => {
              setIsUpload(false);
              onUploadPaymentReceived(id as number);
            }}
          />
          <PaymentReceivedModal
            dataModal={dataModal}
            handleOk={function (): void {
              setDataModal({ ..._modal });
              refetch();
            }}
            handleCancel={function (): void {
              setDataModal({ ..._modal });
            }}
          />
        </div>
      </Card>
    </>
  );
};

export default StockReport;
